import {
  Badge,
  Body1,
  Button,
  Spinner,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  bundleIcon,
  ChevronRight16Regular,
  Database24Filled,
  Database24Regular,
} from '@fluentui/react-icons';
import { useCallback, useMemo } from 'react';

import type { Dataset } from '../../../services/dataHubService.interface.js';

import type { DatasetListProps } from './DatasetList.interface.js';
import { useStyles } from './DatasetList.style.js';

const DatabaseIcon = bundleIcon(
  Database24Filled,
  Database24Regular,
) as React.FC;

// ── Row ───────────────────────────────────────────────────────────────────────

interface DatasetRowProps {
  dataset: Dataset;
  isSelected?: boolean;
  onClick?: (dataset: Dataset) => void;
}

function DatasetRow({ dataset, isSelected, onClick }: DatasetRowProps) {
  const styles = useStyles();

  const handleClick = useCallback(() => {
    onClick?.(dataset);
  }, [dataset, onClick]);

  const statusColor =
    dataset.status === 'Published'
      ? ('success' as const)
      : dataset.status === 'Versioned'
        ? ('subtle' as const)
        : ('important' as const);

  const accessColor =
    dataset.accessLevel === 'Restricted'
      ? ('warning' as const)
      : ('informative' as const);

  const formattedRowCount =
    dataset.rowCount !== undefined
      ? dataset.rowCount >= 1000
        ? `${(dataset.rowCount / 1000).toFixed(1)}k rows`
        : `${dataset.rowCount} rows`
      : null;

  const updatedDate = dataset.updatedAt ?? dataset.createdAt;
  const formattedDate = updatedDate
    ? new Date(updatedDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

  const tooltipContent = useMemo(() => {
    const items: { label: string; value: string }[] = [];
    items.push({ label: 'Name', value: dataset.name });
    if (dataset.description)
      items.push({ label: 'Description', value: dataset.description });
    items.push({ label: 'Status', value: dataset.status });
    if (dataset.accessLevel)
      items.push({ label: 'Access', value: dataset.accessLevel });
    if (dataset.dataType)
      items.push({ label: 'Type', value: dataset.dataType });
    items.push({ label: 'Version', value: String(dataset.version) });
    if (formattedRowCount)
      items.push({ label: 'Rows', value: formattedRowCount });
    if (dataset.ownerAlias)
      items.push({ label: 'Owner', value: dataset.ownerAlias });
    if (dataset.creatorName)
      items.push({ label: 'Creator', value: dataset.creatorName });
    if (formattedDate) items.push({ label: 'Updated', value: formattedDate });
    if (dataset.tags && dataset.tags.length > 0)
      items.push({ label: 'Tags', value: dataset.tags.join(', ') });
    return items;
  }, [dataset, formattedRowCount, formattedDate]);

  return (
    <Tooltip
      content={{
        children: (
          <div className={styles.tooltipContent}>
            {tooltipContent.map((item) => (
              <div key={item.label}>
                <strong>{item.label}:</strong> {item.value}
              </div>
            ))}
          </div>
        ),
      }}
      relationship="description"
      positioning="after"
      showDelay={500}
    >
      <button
        type="button"
        className={mergeClasses(styles.row, isSelected && styles.rowSelected)}
        onClick={handleClick}
        aria-label={`Open dataset ${dataset.name}`}
        aria-current={isSelected ? 'page' : undefined}
      >
        <div className={styles.rowMain}>
          <div className={styles.rowNameLine}>
            <span className={styles.rowName}>{dataset.name}</span>
            {dataset.dataType && (
              <Badge appearance="tint" color="brand" size="small">
                {dataset.dataType}
              </Badge>
            )}
            {dataset.accessLevel && (
              <Badge appearance="tint" color={accessColor} size="small">
                {dataset.accessLevel}
              </Badge>
            )}
            <Badge appearance="filled" color={statusColor} size="small">
              {dataset.status}
            </Badge>
            {dataset.tags?.slice(0, 3).map((tag) => (
              <Badge key={tag} appearance="outline" color="subtle" size="small">
                {tag}
              </Badge>
            ))}
          </div>

          <div className={styles.rowMeta}>
            {formattedRowCount && (
              <>
                <span className={styles.rowMetaText}>{formattedRowCount}</span>
                <span className={styles.rowMetaSeparator}>·</span>
              </>
            )}
            <span className={styles.rowMetaText}>Updated {formattedDate}</span>
            {dataset.ownerAlias && (
              <>
                <span className={styles.rowMetaSeparator}>·</span>
                <span className={styles.rowMetaText}>{dataset.ownerAlias}</span>
              </>
            )}
          </div>
        </div>

        <div className={styles.rowRight}>
          <ChevronRight16Regular className={styles.chevron} />
        </div>
      </button>
    </Tooltip>
  );
}

// ── List ──────────────────────────────────────────────────────────────────────

export function DatasetList({
  datasets,
  isLoading,
  error,
  selectedId,
  onDatasetClick,
  isLoadingMore = false,
  hasMore = false,
  onLoadMore,
}: DatasetListProps) {
  const styles = useStyles();

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading datasets..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorState}>
        <Body1>Failed to load datasets: {error}</Body1>
      </div>
    );
  }

  if (datasets.length === 0) {
    return (
      <div className={styles.emptyState}>
        <DatabaseIcon />
        <Body1>No datasets match your filters.</Body1>
      </div>
    );
  }

  return (
    <div className={styles.list} role="list" aria-label="Datasets">
      {datasets.map((dataset) => (
        <DatasetRow
          key={dataset.id}
          dataset={dataset}
          isSelected={dataset.id === selectedId}
          onClick={onDatasetClick}
        />
      ))}
      {hasMore && (
        <div className={styles.loadMoreRow}>
          <Button
            appearance="subtle"
            disabled={isLoadingMore}
            onClick={onLoadMore}
          >
            {isLoadingMore ? 'Loading...' : 'Load more'}
          </Button>
        </div>
      )}
    </div>
  );
}
